import { t, useLanguage } from '../../i18n';
import React, { useState, useEffect, useContext } from 'react';
import { ThemePreferencesContext } from '../../contexts/ThemeContext';
import { View, StyleSheet, FlatList, TouchableOpacity, Alert } from 'react-native';
import { Text, Card, Button, useTheme, ActivityIndicator, FAB } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { collection, query, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { Course } from '../../types';

const MyCoursesScreen = ({ navigation }: any) => {
  useLanguage();
    const { toggleTheme, isDarkMode } = useContext(ThemePreferencesContext);

    const [courses, setCourses] = useState<Course[]>([]);
    const [loading, setLoading] = useState(true);
    const theme = useTheme();

    useEffect(() => {
        const unsubscribe = navigation.addListener('focus', () => {
            fetchMyCourses();
        });

        return unsubscribe;
    }, [navigation]);

    const fetchMyCourses = async () => {
        if (!auth.currentUser) return;

        try {
            setLoading(true);
            const coursesRef = collection(db, 'users', auth.currentUser.uid, 'courses');
            const q = query(coursesRef);
            const querySnapshot = await getDocs(q);

            const coursesData = querySnapshot.docs.map(doc => ({
                userCourseId: doc.id,
                ...doc.data()
            })) as any[];

            setCourses(coursesData);
        } catch (error) {
            console.error('Error fetching my courses:', error);
            Alert.alert(t("エラー"), t("データの取得に失敗しました"));
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveCourse = async (userCourseId: string, courseName: string) => {
        if (!auth.currentUser) return;

        Alert.alert(
            t("確認"),
            t("{0}を時間割から削除しますか？", [courseName]),
            [
                { text: t("キャンセル"), style: 'cancel' },
                {
                    text: t("削除"),
                    style: 'destructive',
                    onPress: async () => {
                        if (!auth.currentUser) return;
                        try {
                            await deleteDoc(doc(db, 'users', auth.currentUser.uid, 'courses', userCourseId));
                            setCourses(prev => prev.filter(c => (c as any).userCourseId !== userCourseId));
                        } catch (error) {
                            console.error('Error removing course:', error);
                            Alert.alert(t("エラー"), t("削除に失敗しました"));
                        }
                    }
                }
            ]
        );
    };

    const dayLabels = ['', '月', '火', '水', '木', '金', '土', '日'];

    const renderItem = ({ item }: { item: Course }) => (
        <Card
            style={styles.card}
            onPress={() => navigation.navigate('CourseDetail', { course: item })}
        >
            <Card.Content>
                <View style={styles.cardHeader}>
                    <Text style={styles.courseName}>{item.name}</Text>
                    <TouchableOpacity onPress={() => handleRemoveCourse((item as any).userCourseId, item.name)}>
                        <Ionicons name="trash-outline" size={20} color={theme.colors.error} />
                    </TouchableOpacity>
                </View>
                <Text style={styles.professorName}>{item.professor}</Text>
                <View style={styles.infoRow}>
                    <Text style={styles.infoText}>
                        {item.dayOfWeek && item.period ? t('{0}曜{1}限', [t(dayLabels[item.dayOfWeek]), item.period]) : t('不定期')}
                    </Text>
                    <Text style={styles.infoText}>{item.room}</Text>
                </View>
            </Card.Content>
        </Card>
    );

    return (
        <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
            <View style={styles.header}>
                <Text style={[styles.title, { color: theme.colors.primary }]}>{t("登録済み授業")}</Text>
                <TouchableOpacity onPress={toggleTheme}>
                    <Ionicons name={isDarkMode ? 'sunny' : 'moon'} size={24} color={theme.colors.primary} />
                </TouchableOpacity>
            </View>

            {loading ? (
                <View style={styles.centerContainer}>
                    <ActivityIndicator animating={true} color={theme.colors.primary} />
                </View>
            ) : courses.length === 0 ? (
                <View style={styles.centerContainer}>
                    <Ionicons name="book-outline" size={64} color={theme.colors.primary} style={{ opacity: 0.3 }} />
                    <Text style={styles.emptyText}>{t("登録されている授業はありません")}</Text>
                    <Button
                        mode="contained"
                        onPress={() => navigation.navigate('Courses')}
                        style={styles.browseButton}
                    >
                        {t("シラバスを見てみる")}
                    </Button>
                </View>
            ) : (
                <FlatList
                    data={courses}
                    renderItem={renderItem}
                    keyExtractor={(item: any) => item.userCourseId}
                    contentContainerStyle={styles.listContent}
                    style={{ flex: 1 }}
                />
            )}

            <FAB
                style={[styles.fab, { backgroundColor: theme.colors.primary }]}
                icon="plus"
                onPress={() => navigation.navigate('Courses')}
                label={t("授業を追加")}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold',
    },
    centerContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    listContent: {
        padding: 16,
        paddingBottom: 80,
    },
    card: {
        marginBottom: 12,
        elevation: 2,
    },
    cardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    courseName: {
        fontSize: 18,
        fontWeight: 'bold',
        flex: 1,
    },
    professorName: {
        fontSize: 14,
        opacity: 0.7,
        marginTop: 4,
    },
    infoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 8,
    },
    infoText: {
        fontSize: 12,
        opacity: 0.5,
    },
    emptyText: {
        marginTop: 16,
        fontSize: 16,
        opacity: 0.5,
    },
    browseButton: {
        marginTop: 24,
    },
    fab: {
        position: 'absolute',
        margin: 16,
        right: 0,
        bottom: 0,
    },
});

export default MyCoursesScreen;
